import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck, CheckCircle2, XCircle, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [user, setUser] = useState(authAPI.getCurrentUser());
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const currentUser = authAPI.getCurrentUser();
    if (!currentUser || !authAPI.isAuthenticated()) {
      navigate("/login", { replace: true });
      return;
    }
    setUser(currentUser);
  }, [navigate]);

  const isMinLength = password.length >= 8;
  const isDifferentFromDefault = password !== "Memoire@2026";
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const isFormValid = isMinLength && isDifferentFromDefault && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isMinLength) {
      toast.error("A nova senha deve ter no mínimo 8 caracteres.");
      return;
    }

    if (!isDifferentFromDefault) {
      toast.error("A nova senha deve ser diferente da senha padrão.");
      return;
    }

    if (!passwordsMatch) {
      toast.error("As senhas informadas não coincidem.");
      return;
    }

    setLoading(true);

    try {
      await authAPI.changePassword(password);
      toast.success("Senha alterada com sucesso! Bem-vindo(a) ao Memoire.");

      // Obter usuário atualizado
      const updatedUser = authAPI.getCurrentUser();
      if (updatedUser?.tipo_usuario === "admin" || updatedUser?.tipo_usuario === "professor") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/aluno", { replace: true });
      }
    } catch (error: unknown) {
      console.error("Change password error:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao alterar a senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    toast.info("Sessão encerrada.");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#013079] to-[#1a4a9e] p-4">
      <Card className="w-full max-w-md shadow-2xl bg-white border border-gray-200 animate-scale-in">
        <CardHeader className="text-center space-y-2 pb-4">
          <div className="mx-auto mb-2 flex justify-center">
            <img
              src="https://img.codigo1615.com.br/uploads/images/memoire/img_692461aa0c0ed3.98647780.png"
              alt="Memoire"
              className="h-20 w-auto object-contain"
              referrerPolicy="no-referrer"
            />
          </div>
          <div className="inline-flex items-center justify-center gap-2 text-[#013079] font-bold text-2xl">
            <ShieldCheck className="w-6 h-6 text-amber-500" />
            <CardTitle className="text-2xl text-[#013079]">Troca de Senha Obrigatória</CardTitle>
          </div>
          <CardDescription className="text-gray-600 text-sm">
            {user?.nome ? `Olá, ${user.nome}! ` : ""}
            Por segurança, defina uma nova senha pessoal para continuar acessando a plataforma.
          </CardDescription>
          {user?.email && (
            <div className="inline-block bg-blue-50 border border-blue-200 text-[#013079] text-xs px-3 py-1 rounded-full font-medium">
              {user.email}
            </div>
          )}
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-gray-700 font-medium">
                Nova Senha
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 8 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-gray-700 font-medium">
                Confirmar Nova Senha
              </Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Repita a nova senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 pr-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Checklist de Validação */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-xs border border-gray-200">
              <p className="font-semibold text-gray-700 mb-1">Requisitos da nova senha:</p>
              <div className="flex items-center gap-2">
                {isMinLength ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-400" />
                )}
                <span className={isMinLength ? "text-green-700 font-medium" : "text-gray-600"}>
                  Pelo menos 8 caracteres
                </span>
              </div>
              <div className="flex items-center gap-2">
                {password.length > 0 && isDifferentFromDefault ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-400" />
                )}
                <span className={password.length > 0 && isDifferentFromDefault ? "text-green-700 font-medium" : "text-gray-600"}>
                  Diferente da senha inicial padrão
                </span>
              </div>
              <div className="flex items-center gap-2">
                {passwordsMatch ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-400" />
                )}
                <span className={passwordsMatch ? "text-green-700 font-medium" : "text-gray-600"}>
                  As duas senhas são iguais
                </span>
              </div>
            </div>

            <Button
              type="submit"
              disabled={!isFormValid || loading}
              className="w-full h-11 bg-[#013079] hover:bg-[#012060] text-white font-medium transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando nova senha...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Definir Nova Senha e Entrar
                </>
              )}
            </Button>

            <div className="pt-2 text-center">
              <Button
                type="button"
                variant="ghost"
                onClick={handleLogout}
                className="text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors gap-1.5"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sair da conta
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
